import { useMutation } from "@tanstack/react-query";
import type { PantryIntakeItem, PantryIntakeMode } from "@batch-and-bowl/shared";
import { extractPantryIntake, lookupPantryItemByBarcode } from "../api/pantry-intake";
import { track } from "../analytics";
import { extractErrorStatus } from "../errors";

export interface PantryIntakeInput {
  file: File;
  mode: PantryIntakeMode;
}

export function usePantryIntake() {
  return useMutation<PantryIntakeItem[], Error, PantryIntakeInput>({
    mutationFn: ({ file, mode }) => extractPantryIntake(file, mode),
    onMutate: ({ mode }) => {
      track("pantry_scan_attempted", { mode });
    },
    onSuccess: (items, { mode }) => {
      track("pantry_scan_succeeded", { mode, itemCount: items.length });
    },
    onError: (error, { mode }) => {
      const status = extractErrorStatus(error);
      track("pantry_scan_failed", { mode, status });
    },
  });
}

export interface PantryBarcodeIntakeInput {
  upc: string;
  cameraSupported: boolean;
  captureMethod: "camera" | "manual";
}

/** Pantry v2, B4 (issue #376): barcode/UPC lookup. */
export function usePantryBarcodeIntake() {
  return useMutation<PantryIntakeItem[], Error, PantryBarcodeIntakeInput>({
    mutationFn: ({ upc }) => lookupPantryItemByBarcode(upc),
    onMutate: ({ cameraSupported, captureMethod }) => {
      track("pantry_scan_attempted", {
        mode: "barcode",
        cameraSupported,
        captureMethod,
      });
    },
    onSuccess: (items) => {
      const codeFamily = items[0]?.codeFamily ?? "unknown";
      track("pantry_scan_succeeded", {
        mode: "barcode",
        itemCount: items.length,
        codeFamily,
      });
    },
    onError: (error) => {
      const status = extractErrorStatus(error);
      track("pantry_scan_failed", { mode: "barcode", status });
    },
  });
}
